import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { applyPlanningSmb } from '../api'
import { SMB_CURRENCY_USD, SMB_FY_QUARTER } from '../configs'
import { wouldSmbEditDiscardGenuineData } from '../lib/applySmbEdit'
import { flattenSmbTree } from '../lib/buildSmbTree'
import {
  buildGuidedSmbWizardStore,
  createGuidedSmbWizardDraft,
  editGuidedSmbWizardDraft,
  getGuidedSmbWizardRows,
  getGuidedSmbWizardValue,
  getGuidedSmbYears,
  isSameGuidedSmbRows,
  normalizeGuidedSmbGrowth,
} from '../lib/guidedSmbWizard'
import { isSmbNodeExcluded } from '../lib/isSmbNodeExcluded'
import { mapPlanningSmbToStore, mapStoreToPlanningSmbRows } from '../lib/mapPlanningSmb'
import { preservePlanningSmbUnshownYears } from '../lib/planningSmbYears'
import { getDirectStoredSplit } from '../lib/smbSplitStore'
import { IPlanningSmbGuidedWizardProps, TPlanningSmbGuidedScreen } from '../model/guidedWizardTypes'
import { ISmbNode } from '../model/types'

export const usePlanningSmbGuidedWizard = (props: IPlanningSmbGuidedWizardProps) => {
  const { onClose } = props
  const [initialDraft] = useState(() => createGuidedSmbWizardDraft(props))
  const [draft, setDraft] = useState(initialDraft)
  const [screen, setScreen] = useState<TPlanningSmbGuidedScreen>(
    props.offeringSummary.isEmpty ? 'start' : 'overview'
  )
  const [showFinancialData, setShowFinancialData] = useState(false)
  const [showQuarters, setShowQuarters] = useState(
    props.isQuarterlyPlanning || props.isQuarterlyPlanningDisabled
  )
  const [showCategories, setShowCategories] = useState(false)
  const [expandedNodes, setExpandedNodes] = useState<ReadonlySet<string>>(new Set())
  const [metric, setMetric] = useState(props.planActualsType)
  const [currency, setCurrency] = useState(props.planCurrency)
  const [isApplying, setIsApplying] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [isConfirming, setIsConfirming] = useState(false)
  const busy = useRef(false)
  const mounted = useRef(true)
  const confirmation = useRef<((accepted: boolean) => void) | null>(null)
  const years = useMemo(
    () => getGuidedSmbYears(props.fYear, props.lastPlanYear),
    [props.fYear, props.lastPlanYear]
  )
  const nodes = useMemo(
    () => flattenSmbTree(props.tree).filter((node) => !isSmbNodeExcluded(node)),
    [props.tree]
  )
  const categories = useMemo(
    () => props.tree.children.filter((node) => !isSmbNodeExcluded(node)),
    [props.tree]
  )
  const context = useMemo(
    () => ({ partnerId: props.partnerId, programType: props.programType, years }),
    [props.partnerId, props.programType, years]
  )
  const initialStore = useMemo(
    () => mapPlanningSmbToStore(props.planningSmb, context, props.tree),
    [props.planningSmb, context, props.tree]
  )
  const store = useMemo(() => buildGuidedSmbWizardStore(props, draft), [props, draft])
  const getValue = useCallback(
    (node: ISmbNode, year: number, fq = SMB_FY_QUARTER) =>
      getGuidedSmbWizardValue(props, store, node, year, fq),
    [props, store]
  )
  const isCustom = useCallback(
    (node: ISmbNode, year: number, fq = SMB_FY_QUARTER) =>
      !!getDirectStoredSplit(draft.overrides, node.id, year, fq),
    [draft.overrides]
  )
  const isDirty =
    JSON.stringify(draft) !== JSON.stringify(initialDraft) ||
    !years.every((year) => !getGuidedSmbWizardValue(props, initialStore, props.tree, year).isEmpty)

  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
      confirmation.current?.(false)
      confirmation.current = null
    }
  }, [])

  const close = useCallback(() => {
    if (!busy.current) onClose()
  }, [onClose])
  const changeScreen = useCallback((next: TPlanningSmbGuidedScreen) => {
    if (!busy.current) setScreen(next)
  }, [])
  const edit = useCallback((node: ISmbNode, year: number, fq: number, smb: number | null) => {
    if (!busy.current)
      setDraft((previous) => editGuidedSmbWizardDraft(previous, node, year, fq, smb))
  }, [])
  const setGrowth = useCallback((growth: number) => {
    if (!busy.current)
      setDraft((previous) => ({ ...previous, growth: normalizeGuidedSmbGrowth(growth) }))
  }, [])
  const toggleExpanded = useCallback(
    (node: ISmbNode) =>
      setExpandedNodes((previous) => {
        const next = new Set(previous)
        if (next.has(node.id)) next.delete(node.id)
        else next.add(node.id)
        return next
      }),
    []
  )
  const resolveConfirmation = useCallback((accepted: boolean) => {
    const resolve = confirmation.current
    confirmation.current = null
    setIsConfirming(false)
    resolve?.(accepted)
  }, [])

  const save = useCallback(async () => {
    if (busy.current) return
    if (!isDirty) {
      props.onClose()
      return
    }
    busy.current = true
    setIsApplying(true)
    try {
      const original = mapStoreToPlanningSmbRows(initialStore, props.tree, context)
      let rows = getGuidedSmbWizardRows(props, store)
      const originalYear = original.filter((row) => row.FYear === props.fYear)
      const nextYear = rows.filter((row) => row.FYear === props.fYear)
      if (
        !isSameGuidedSmbRows(originalYear, nextYear) &&
        wouldSmbEditDiscardGenuineData(initialStore, props.tree, props.fYear, SMB_FY_QUARTER)
      ) {
        const accepted = await new Promise<boolean>((resolve) => {
          confirmation.current = resolve
          setIsConfirming(true)
        })
        if (!mounted.current) return
        if (!accepted) rows = [...originalYear, ...rows.filter((row) => row.FYear !== props.fYear)]
      }
      if (!isSameGuidedSmbRows(original, rows)) {
        setIsSaving(true)
        await applyPlanningSmb({
          partnerId: props.partnerId,
          fYear: props.fYear,
          rows: preservePlanningSmbUnshownYears(rows, props.planningSmb, props),
        })
        await props.onSaved()
      }
      if (mounted.current) props.onClose()
    } catch (error) {
      if (mounted.current) props.onSaveFailed(error)
    } finally {
      busy.current = false
      if (mounted.current) {
        setIsApplying(false)
        setIsSaving(false)
      }
    }
  }, [props, initialStore, context, store, isDirty])

  const getFinancialAmount = useCallback(
    (node: ISmbNode, year: number, fq = SMB_FY_QUARTER): number => {
      const resolve = (candidate: ISmbNode): number => {
        if (isSmbNodeExcluded(candidate)) return 0
        const isActual = year < props.fYear
        if (candidate.settingsIndex >= 0 && (!isActual || !candidate.children.length)) {
          const value = props.getFinancialValue({
            settingsIndex: candidate.settingsIndex,
            fq,
            type: isActual ? 'actuals' : 'targets',
            fYear: year,
            actualsType: metric,
            isUSD: currency === SMB_CURRENCY_USD,
            smbFraction: 'Total',
          })
          return typeof value === 'number' && Number.isFinite(value) ? value : 0
        }
        return candidate.children.reduce((total, child) => total + resolve(child), 0)
      }
      return resolve(node)
    },
    [currency, metric, props]
  )

  return {
    draft,
    screen,
    changeScreen,
    years,
    nodes,
    categories,
    store,
    getValue,
    isCustom,
    edit,
    setGrowth,
    showFinancialData,
    setShowFinancialData,
    showQuarters,
    setShowQuarters: (value: boolean) => {
      if (!props.isQuarterlyPlanningDisabled) setShowQuarters(value)
    },
    showCategories,
    setShowCategories,
    expandedNodes,
    toggleExpanded,
    metric,
    setMetric,
    currency,
    setCurrency,
    isApplying,
    isSaving,
    isConfirming,
    resolveConfirmation,
    save,
    close,
    getFinancialAmount,
  }
}

export type TPlanningSmbGuidedWizard = ReturnType<typeof usePlanningSmbGuidedWizard>
